import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { Search, Printer, User, BookOpen, MapPin, Calendar, FileText, Eye, ZoomIn, ZoomOut, Maximize2, Minimize2, ArrowLeft, X, Clock, CheckCircle } from "lucide-react";
import api from "../api";
import puentesLogo from "../assets/img/puentes-logo.png";
import utnLogo from "../assets/img/utn-logo.png";
import signatureYacoy from "../assets/img/signature_yacoy.png";

const CustomCertificateModal = ({ onClose }) => {
  const [view, setView] = useState("pending"); // "pending" | "form" | "preview"
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Student selection
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [diplomas, setDiplomas] = useState([]);
  const [selectedDiploma, setSelectedDiploma] = useState(null);

  // Student data editing
  const [editDni, setEditDni] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  // Certificate content
  const [certificateTitle, setCertificateTitle] = useState("CONSTANCIA");
  const [customText, setCustomText] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Preview
  const [zoom, setZoom] = useState(0.8);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await api.get("custom-certificate-requests/");
      setPendingRequests(res.data.filter(r => r.status === "PE"));
    } catch (err) {
      console.error("Error fetching custom requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoadingStudents(true);
      const res = await api.get("users/students/");
      setStudents(res.data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleSelectFromRequest = async (req) => {
    setSelectedRequest(req);
    setEditDni(req.student_dni || "");
    setEditFirstName(req.student_name?.split(" ")[0] || "");
    setEditLastName(req.student_name?.split(" ").slice(1).join(" ") || "");
    setMunicipality(req.municipality || "");
    setCustomText("");
    setCertificateTitle("CONSTANCIA");

    try {
      const res = await api.get("diplomas/");
      setDiplomas(res.data);
      setSelectedDiploma(res.data.find(d => d.id === req.diploma) || null);
    } catch (err) {
      console.error("Error fetching diplomas:", err);
    }

    // Fetch student full data
    try {
      const studentRes = await api.get(`users/students/`);
      const student = studentRes.data.find(s => s.email === req.student_email);
      if (student) {
        setSelectedStudent(student);
        setEditDni(student.profile?.dni || req.student_dni || "");
        setEditFirstName(student.first_name || "");
        setEditLastName(student.last_name || "");
        if (student.municipio) setMunicipality(student.municipio);
      }
    } catch (err) {
      console.error(err);
    }

    setView("form");
  };

  const handleNewCertificate = async () => {
    setSelectedRequest(null);
    setSelectedStudent(null);
    setSearchTerm("");
    setCustomText("");
    setCertificateTitle("CONSTANCIA");
    setMunicipality("");
    setSelectedDiploma(null);
    fetchStudents();
    setView("form");
  };

  const handleStudentSelect = async (student) => {
    setSelectedStudent(student);
    setEditDni(student.profile?.dni || "");
    setEditFirstName(student.first_name || "");
    setEditLastName(student.last_name || "");
    setSearchTerm("");
    if (student.municipio) setMunicipality(student.municipio);
    try {
      const res = await api.get("diplomas/");
      setDiplomas(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateAndPreview = async () => {
    if (!editDni || !editFirstName || !editLastName) {
      setUpdateError("Todos los campos personales son obligatorios.");
      return;
    }
    if (!customText.trim()) {
      setUpdateError("El texto de la constancia no puede estar vacío.");
      return;
    }

    setIsUpdating(true);
    setUpdateError("");
    try {
      if (selectedStudent) {
        await api.patch(`users/${selectedStudent.id}/update_student_data/`, {
          dni: editDni,
          first_name: editFirstName,
          last_name: editLastName,
        });
        setSelectedStudent({
          ...selectedStudent,
          first_name: editFirstName,
          last_name: editLastName,
          profile: { ...selectedStudent.profile, dni: editDni },
        });
      }

      if (selectedRequest) {
        await api.post(`custom-certificate-requests/${selectedRequest.id}/issue/`, {
          aco_custom_text: customText,
          certificate_title: certificateTitle,
        });
        await fetchPendingRequests();
      }

      // Separado en dos commits: apagar el spinner del botón y desmontar todo
      // el formulario (al cambiar view, que saca ese mismo botón del árbol)
      // en el mismo commit de React producía "insertBefore: node is not a
      // child of this node" al reconciliar ambos cambios juntos.
      setIsUpdating(false);
      await new Promise((resolve) => setTimeout(resolve, 0));
      setView("preview");
    } catch (err) {
      setUpdateError("Error al procesar la constancia.");
      console.error(err);
      setIsUpdating(false);
    }
  };

  const handleReject = async (req) => {
    const reason = window.prompt("Motivo del rechazo:");
    if (!reason) return;
    try {
      await api.post(`custom-certificate-requests/${req.id}/reject/`, { reason });
      await fetchPendingRequests();
    } catch (err) {
      console.error(err);
    }
  };

  const filteredStudents = students.filter(
    (s) =>
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.first_name && s.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.last_name && s.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.profile?.dni && s.profile.dni.includes(searchTerm))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    const months = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  };

  const studentFullName = `${editFirstName} ${editLastName}`.trim();

  return (
    <Modal
      show={true}
      onHide={onClose}
      size={isExpanded ? undefined : "xl"}
      fullscreen={isExpanded}
      centered={!isExpanded}
      backdrop="static"
      className={`certificate-main-modal ${view === "preview" ? "preview-active" : ""}`}
    >
      <Modal.Header closeButton className={`border-0 pb-0 ${view === "preview" ? "bg-light py-2" : "glassmorphism-header"}`}>
        <Modal.Title className="w-100">
          {view !== "preview" ? (
            <div key="title-form" className="d-flex align-items-center gap-2">
              <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary">
                <FileText size={24} />
              </div>
              <div>
                <h5 className="mb-0 fw-bold">Generar Constancia Personalizada</h5>
                <small className="text-muted fw-normal">
                  {view === "pending" ? "Solicitudes pendientes y nueva constancia" : "Completá los datos del certificado"}
                </small>
              </div>
            </div>
          ) : (
            <div key="title-preview" className="d-flex align-items-center justify-content-between w-100 pe-4">
              <span className="fw-bold text-primary fs-5">Vista Previa — {certificateTitle}</span>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-1 bg-white border rounded px-2 py-1 shadow-sm mx-3">
                  <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(Math.max(0.3, zoom - 0.1))}><ZoomOut size={16} /></Button>
                  <span className="small fw-bold text-muted mx-1" style={{ minWidth: "40px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                  <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(Math.min(1.5, zoom + 0.1))}><ZoomIn size={16} /></Button>
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="d-flex align-items-center justify-content-center p-1" style={{ width: "32px", height: "32px" }}>
                  {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </Button>
              </div>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className={view === "preview" ? "p-0 bg-dark bg-opacity-75 d-flex justify-content-center overflow-auto preview-body" : "p-4 form-body"}
        style={view === "preview" ? { height: isExpanded ? "calc(100vh - 120px)" : "80vh" } : {}}
      >
        {/* VISTA: PENDIENTES */}
        {view === "pending" && (
          <div key="body-pending">
            <div className="mb-4">
              <Button variant="primary" className="d-flex align-items-center gap-2 shadow-sm" onClick={handleNewCertificate}>
                <FileText size={18} /> Nueva Constancia Personalizada (sin solicitud previa)
              </Button>
            </div>

            <h6 className="fw-bold text-muted text-uppercase small mb-3 d-flex align-items-center gap-2">
              <Clock size={16} /> Solicitudes Pendientes de Alumnos
            </h6>

            {loadingRequests ? (
              <div className="text-center p-4"><Spinner animation="border" variant="primary" /></div>
            ) : pendingRequests.length === 0 ? (
              <div className="text-center p-5 bg-white rounded-4 border text-muted">
                <CheckCircle size={40} className="mb-2 opacity-25" />
                <p className="mb-0">No hay solicitudes pendientes.</p>
              </div>
            ) : (
              <div className="border rounded shadow-sm bg-white overflow-auto" style={{ maxHeight: "400px" }}>
                {pendingRequests.map((req) => (
                  <div key={req.id} className="p-3 border-bottom d-flex align-items-start justify-content-between gap-3">
                    <div className="d-flex align-items-start gap-3 flex-grow-1">
                      <div className="p-2 bg-warning bg-opacity-10 rounded-circle text-warning mt-1">
                        <User size={18} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{req.student_name}</div>
                        <div className="small text-muted mb-1">{req.student_email} · {req.diploma_name}</div>
                        <div className="p-2 bg-light rounded border-start border-3 border-info">
                          <small className="text-muted fw-bold d-block mb-1">Descripción del alumno:</small>
                          <small className="text-dark">{req.student_description}</small>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2 flex-shrink-0">
                      <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={() => handleSelectFromRequest(req)}>
                        <FileText size={13} className="me-1" /> Generar
                      </Button>
                      <Button variant="outline-danger" size="sm" className="rounded-pill px-3" onClick={() => handleReject(req)}>
                        <X size={13} className="me-1" /> Rechazar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VISTA: FORMULARIO */}
        {view === "form" && (
          <div key="body-form" className="certificate-form-content">
            {/* Selección de alumno (solo si no viene de un pedido) */}
            {!selectedRequest && !selectedStudent && (
              <div className="search-section animate-fade-in mb-4">
                <Form.Group>
                  <Form.Label className="fw-bold small text-uppercase text-muted">1. Buscar Alumno</Form.Label>
                  <div className="position-relative">
                    <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                    <Form.Control
                      type="text"
                      placeholder="Buscar por nombre, email o DNI..."
                      className="ps-5 py-3 shadow-sm border-0 bg-light"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoFocus
                    />
                  </div>
                </Form.Group>
                <div className="student-list border rounded shadow-sm bg-white overflow-auto mt-2" style={{ maxHeight: "280px" }}>
                  {loadingStudents ? (
                    <div className="p-4 text-center"><Spinner animation="border" variant="primary" /></div>
                  ) : filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <div
                        key={student.id}
                        className="p-3 border-bottom list-item-hover cursor-pointer d-flex align-items-center justify-content-between"
                        onClick={() => handleStudentSelect(student)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary"><User size={18} /></div>
                          <div>
                            <div className="fw-bold">{student.first_name} {student.last_name}</div>
                            <div className="small text-muted">{student.email} · DNI: {student.profile?.dni || "Faltante"}</div>
                          </div>
                        </div>
                        <Button variant="outline-primary" size="sm">Seleccionar</Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted">No se encontraron alumnos.</div>
                  )}
                </div>
              </div>
            )}

            {/* Alumno seleccionado o desde pedido */}
            {(selectedStudent || selectedRequest) && (
              <div className="form-section animate-fade-in">
                {/* Badge del alumno */}
                <div className="selected-student-badge p-3 bg-light rounded mb-4 d-flex align-items-center justify-content-between border">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary"><User size={20} /></div>
                    <div>
                      <div className="fw-bold">{selectedStudent?.first_name || editFirstName} {selectedStudent?.last_name || editLastName}</div>
                      <div className="small text-muted">{selectedStudent?.email || selectedRequest?.student_email}</div>
                    </div>
                  </div>
                  {!selectedRequest && (
                    <Button variant="link" size="sm" onClick={() => setSelectedStudent(null)} className="text-decoration-none">
                      Cambiar alumno
                    </Button>
                  )}
                </div>

                {/* Descripción del pedido (si viene de uno) */}
                {selectedRequest && (
                  <div className="p-3 bg-info bg-opacity-10 rounded border border-info mb-4">
                    <small className="text-info fw-bold text-uppercase d-block mb-1">Descripción del alumno:</small>
                    <p className="mb-0 text-dark">{selectedRequest.student_description}</p>
                  </div>
                )}

                {updateError && <Alert variant="danger">{updateError}</Alert>}

                <Row className="g-4">
                  {/* Datos personales */}
                  <Col md={12}>
                    <div className="p-3 bg-info bg-opacity-10 rounded border border-info mb-2">
                      <h6 className="fw-bold text-info mb-3 d-flex align-items-center gap-2"><User size={18} /> Validar Datos Personales</h6>
                      <Row className="g-3">
                        <Col md={4}>
                          <Form.Label className="small fw-bold">Nombre</Form.Label>
                          <Form.Control value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} placeholder="Nombre" />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small fw-bold">Apellido</Form.Label>
                          <Form.Control value={editLastName} onChange={(e) => setEditLastName(e.target.value)} placeholder="Apellido" />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small fw-bold text-danger">DNI (Requerido)</Form.Label>
                          <Form.Control value={editDni} onChange={(e) => setEditDni(e.target.value)} placeholder="DNI sin puntos" className={!editDni ? "border-danger" : ""} />
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Diplomatura */}
                  {!selectedRequest && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2"><BookOpen size={16} /> Diplomatura</Form.Label>
                        <Form.Select className="py-3 shadow-sm border-0 bg-light" onChange={(e) => setSelectedDiploma(diplomas.find((d) => d.id == e.target.value))} value={selectedDiploma?.id || ""}>
                          <option value="">Selecciona una diplomatura...</option>
                          {diplomas.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}

                  {/* Municipio y fecha */}
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2"><MapPin size={16} /> Municipio</Form.Label>
                      <Form.Control type="text" className="py-3 shadow-sm border-0 bg-light" placeholder="Ej: Mercedes" value={municipality} onChange={(e) => setMunicipality(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2"><Calendar size={16} /> Fecha de Emisión</Form.Label>
                      <Form.Control type="date" className="py-3 shadow-sm border-0 bg-light" value={date} onChange={(e) => setDate(e.target.value)} />
                    </Form.Group>
                  </Col>

                  {/* Título de la constancia */}
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2"><FileText size={16} /> Título de la Constancia</Form.Label>
                      <Form.Control
                        type="text"
                        className="py-3 shadow-sm border-0 bg-light"
                        placeholder='Ej: CONSTANCIA DE PARTICIPACIÓN, CONSTANCIA DE ASISTENCIA...'
                        value={certificateTitle}
                        onChange={(e) => setCertificateTitle(e.target.value.toUpperCase())}
                      />
                      <Form.Text className="text-muted">Este título aparecerá centrado y destacado en el certificado.</Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Texto personalizado */}
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2">
                        <FileText size={16} /> Texto del Certificado <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        className="shadow-sm border-0 bg-light"
                        placeholder="Redactá el cuerpo de la constancia. Podés usar el nombre del alumno, la diplomatura, etc. Este texto se mostrará tal cual en el certificado oficial..."
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        El nombre completo y DNI del alumno ya aparecen en el encabezado del certificado.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-4 d-flex gap-2">
                  <Button
                    variant="primary"
                    className="w-100 py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                    disabled={!customText.trim() || !editDni || isUpdating || (!selectedRequest && !selectedDiploma)}
                    onClick={handleUpdateAndPreview}
                  >
                    {isUpdating ? <Spinner animation="border" size="sm" /> : <Eye size={20} />}
                    {isUpdating ? "Procesando..." : "Previsualizar Constancia"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* VISTA: PREVIEW */}
        {view === "preview" && (
          <div key="body-preview" className="d-flex justify-content-center w-100 pb-5 h-100">
            <div
              id="certificate-to-print"
              className="certificate-page shadow-lg my-4 bg-white"
              style={{
                width: "210mm",
                height: "297mm",
                backgroundColor: "white",
                color: "black",
                fontFamily: "'Times New Roman', Times, serif",
                position: "relative",
                transform: `scale(${zoom})`,
                transformOrigin: "top center",
                padding: "25mm",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {/* Logos */}
              <div className="d-flex justify-content-between align-items-start mb-5">
                <div className="d-flex flex-column align-items-center" style={{ width: "200px" }}>
                  <img src={puentesLogo} alt="Logo Puentes" style={{ width: "130px", marginBottom: "10px" }} />
                  <div className="text-center" style={{ fontSize: "11px", fontWeight: "bold", lineHeight: "1.2" }}>
                    PUENTES<br />PLAN DE INTEGRACIÓN<br />TERRITORIAL<br />UNIVERSITARIA
                  </div>
                </div>
                <div className="d-flex flex-column align-items-center" style={{ width: "220px" }}>
                  <img src={utnLogo} alt="Logo UTN" style={{ width: "80px", marginBottom: "10px" }} />
                  <div className="text-center" style={{ fontSize: "11px", fontWeight: "bold", lineHeight: "1.2" }}>
                    UNIVERSIDAD TECNOLÓGICA NACIONAL<br />DIPLOMATURAS {new Date().getFullYear()}
                  </div>
                </div>
              </div>

              {/* Título */}
              <div className="text-center mt-5 mb-4 pt-4">
                <h1 className="fw-bold" style={{ fontSize: "26px", textDecoration: "underline" }}>{certificateTitle}</h1>
              </div>

              {/* Identificación del alumno */}
              <div style={{ fontSize: "18px", lineHeight: "1.6", marginBottom: "20px" }}>
                <p style={{ textAlign: "justify" }}>
                  La presente se extiende a nombre de: <strong>{studentFullName.toUpperCase()}</strong>,
                  DNI: <strong>{editDni}</strong>
                  {selectedDiploma || selectedRequest?.diploma_name ? (
                    <>, alumno/a de la Diplomatura en: <strong>{(selectedRequest?.diploma_name || selectedDiploma?.name || "").toUpperCase()}</strong></>
                  ) : null}
                  {municipality ? <>, del Municipio de <strong>{municipality.toUpperCase()}</strong></> : null}.
                </p>
              </div>

              {/* Texto personalizado del ACO */}
              <div className="content" style={{ fontSize: "18px", lineHeight: "1.8" }}>
                <div style={{ textAlign: "justify", whiteSpace: "pre-wrap" }}>
                  {customText}
                </div>
                <div style={{ width: "100%", borderBottom: "2px dashed #000", marginTop: "20px" }}></div>
              </div>

              {/* Fecha */}
              <div className="footer-date mt-4 text-end" style={{ fontSize: "18px", paddingRight: "15mm" }}>
                <p>{municipality ? `${municipality.toUpperCase()}, ` : ""}{formatDate(date)}</p>
              </div>

              {/* Firma */}
              <div className="signature-area d-flex flex-column align-items-center" style={{ position: "absolute", bottom: "60px", left: "0", right: "0" }}>
                <div style={{ width: "350px", textAlign: "center" }}>
                  <img src={signatureYacoy} alt="Firma Lucia Yacoy" style={{ width: "320px", marginBottom: "-10px" }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light border-0">
        <div className="w-100 d-flex gap-2">
          {view === "pending" && (
            <Button key="footer-pending" variant="secondary" onClick={onClose}>Cerrar</Button>
          )}
          {view === "form" && (
            <React.Fragment key="footer-form">
              <Button variant="outline-secondary" onClick={() => setView("pending")} className="d-flex align-items-center gap-2">
                <ArrowLeft size={18} /> Volver
              </Button>
              <Button variant="secondary" onClick={onClose} className="ms-auto">Cerrar</Button>
            </React.Fragment>
          )}
          {view === "preview" && (
            <React.Fragment key="footer-preview">
              <Button variant="outline-secondary" onClick={() => setView("form")} className="d-flex align-items-center gap-2">
                <ArrowLeft size={18} /> Volver a Editar
              </Button>
              <Button variant="primary" size="lg" onClick={() => window.print()} className="d-flex align-items-center gap-2 px-4 fw-bold shadow-sm ms-auto">
                <Printer size={20} /> Imprimir Constancia
              </Button>
            </React.Fragment>
          )}
        </div>
      </Modal.Footer>

      <style>{`
        .list-item-hover:hover { background-color: #f8f9fa; cursor: pointer; }
        .certificate-main-modal.preview-active .modal-content { background: #222 !important; }
        .certificate-page strong { font-weight: 700; }
        @page { size: A4; margin: 0 !important; }
        @media print {
          body { margin: 0 !important; padding: 0 !important; }
          #certificate-to-print {
            transform: none !important; margin: 0 !important; box-shadow: none !important;
            position: fixed !important; top: 0 !important; left: 0 !important;
            width: 210mm !important; height: 297mm !important;
          }
          body > *:not(.modal), .modal-header, .modal-footer, .modal-backdrop { display: none !important; }
          .modal { position: absolute !important; left: 0 !important; top: 0 !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; display: block !important; }
          .modal-dialog { margin: 0 !important; max-width: 100% !important; }
          .modal-content { border: none !important; background: white !important; }
          .modal-body { padding: 0 !important; overflow: visible !important; }
        }
      `}</style>
    </Modal>
  );
};

export default CustomCertificateModal;
